import { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import {
  Routes,
  Route,
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Separator } from "../../components/ui/separator";
import { toast } from "sonner";
import {
  LayoutDashboard,
  CreditCard,
  Tag,
  Building2,
  LogOut,
  Plus,
  Pencil,
  Trash2,
  DollarSign,
  Users,
  MessageCircle,
  Settings,
  MessageSquareText,
} from "lucide-react";
import WhatsappTemplates from "./WhatsappTemplates";

const navItems = [
  { path: "/admin", label: "Dashboard", icon: LayoutDashboard },
  { path: "/admin/plans", label: "Plans", icon: CreditCard },
  { path: "/admin/coupons", label: "Coupons", icon: Tag },
  { path: "/admin/organizations", label: "Organizations", icon: Building2 },
  { path: "/admin/whatsapp", label: "WhatsApp Config", icon: MessageCircle },
  {
    path: "/admin/whatsapp-templates",
    label: "WA Templates",
    icon: MessageSquareText,
  },
];

function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  return (
    <div className="flex min-h-screen bg-background">
      <div className="w-64 h-screen sticky top-0 bg-card border-r border-border hidden lg:flex flex-col z-50">
        <div className="p-6">
          <h1 className="text-xl font-black font-heading text-primary">
            OPTIMUS GYM
          </h1>
          <p className="text-xs text-muted-foreground mt-1">Super Admin</p>
        </div>
        <Separator />
        <ScrollArea className="flex-1 px-3">
          <nav className="space-y-1 py-4">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-all ${isActive ? "bg-primary/10 text-primary border-l-2 border-primary" : "text-muted-foreground hover:text-foreground hover:bg-secondary"}`}
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </ScrollArea>
        <div className="p-4 border-t border-border">
          <Button
            variant="ghost"
            className="w-full justify-start"
            onClick={() => {
              window.location.href = logout();
            }}
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>
      <main className="flex-1 p-6 md:p-8">{children}</main>
    </div>
  );
}

function AdminHome() {
  const [revenue, setRevenue] = useState(null);
  useEffect(() => {
    api
      .get("/admin/revenue")
      .then((r) => setRevenue(r.data))
      .catch(console.error);
  }, []);
  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <h1 className="text-2xl font-bold font-heading">Platform Overview</h1>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-4">
            <DollarSign className="h-5 w-5 text-green-400" />
            <p className="text-2xl font-bold font-heading mt-2">
              {"\u20B9"}
              {(revenue?.totalRevenue || 0).toLocaleString()}
            </p>
            <p className="text-xs text-muted-foreground">Total Revenue</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-4">
            <Building2 className="h-5 w-5 text-blue-400" />
            <p className="text-2xl font-bold font-heading mt-2">
              {revenue?.activeOrganizations || 0}
            </p>
            <p className="text-xs text-muted-foreground">
              Active Organizations
            </p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-4">
            <Users className="h-5 w-5 text-purple-400" />
            <p className="text-2xl font-bold font-heading mt-2">
              {revenue?.totalOrganizations || 0}
            </p>
            <p className="text-xs text-muted-foreground">Total Organizations</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function PlansPage() {
  const [plans, setPlans] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "",
    description: "",
    price: "",
    billingCycle: "Monthly",
    trialDays: "14",
    status: "Active",
  });
  useEffect(() => {
    fetchPlans();
  }, []);
  const fetchPlans = async () => {
    try {
      const r = await api.get("/admin/plans");
      setPlans(r.data.plans);
    } catch {}
  };
  const handleSave = async () => {
    try {
      const data = {
        ...form,
        price: parseFloat(form.price),
        trialDays: parseInt(form.trialDays),
      };
      if (editing) await api.put(`/admin/plans/${editing.id}`, data);
      else await api.post("/admin/plans", data);
      toast.success(editing ? "Updated" : "Created");
      setShowForm(false);
      setEditing(null);
      fetchPlans();
    } catch (err) {
      toast.error("Failed");
    }
  };
  return (
    <div className="space-y-6" data-testid="admin-plans-page">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold font-heading">Subscription Plans</h1>
        <Button
          size="sm"
          className="rounded-sm"
          onClick={() => {
            setEditing(null);
            setForm({
              name: "",
              description: "",
              price: "",
              billingCycle: "Monthly",
              trialDays: "14",
              status: "Active",
            });
            setShowForm(true);
          }}
          data-testid="add-plan-btn"
        >
          <Plus className="h-4 w-4 mr-1" />
          Add Plan
        </Button>
      </div>
      <Card className="bg-card border-border rounded-sm">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Cycle</TableHead>
              <TableHead>Trial</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {plans.map((p) => (
              <TableRow key={p.id}>
                <TableCell className="font-medium">{p.name}</TableCell>
                <TableCell className="font-mono">
                  {"\u20B9"}
                  {p.price}
                </TableCell>
                <TableCell>{p.billingCycle}</TableCell>
                <TableCell>{p.trialDays}d</TableCell>
                <TableCell>
                  <Badge
                    className={`${p.status === "Active" ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"} border-0 rounded-sm text-xs`}
                  >
                    {p.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => {
                        setEditing(p);
                        setForm({
                          name: p.name,
                          description: p.description || "",
                          price: String(p.price),
                          billingCycle: p.billingCycle,
                          trialDays: String(p.trialDays),
                          status: p.status,
                        });
                        setShowForm(true);
                      }}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive"
                      onClick={async () => {
                        await api.delete(`/admin/plans/${p.id}`);
                        fetchPlans();
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-card border-border rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editing ? "Edit" : "Create"} Plan
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input
                value={form.name}
                onChange={(e) =>
                  setForm((p) => ({ ...p, name: e.target.value }))
                }
                className="rounded-sm mt-1"
                data-testid="plan-name"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) =>
                  setForm((p) => ({ ...p, description: e.target.value }))
                }
                className="rounded-sm mt-1"
              />
            </div>
            <div>
              <Label>Price</Label>
              <Input
                type="number"
                value={form.price}
                onChange={(e) =>
                  setForm((p) => ({ ...p, price: e.target.value }))
                }
                className="rounded-sm mt-1"
                data-testid="plan-price"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Billing Cycle</Label>
                <Select
                  value={form.billingCycle}
                  onValueChange={(v) =>
                    setForm((p) => ({ ...p, billingCycle: v }))
                  }
                >
                  <SelectTrigger className="rounded-sm mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Monthly">Monthly</SelectItem>
                    <SelectItem value="Yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Trial Days</Label>
                <Input
                  type="number"
                  value={form.trialDays}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, trialDays: e.target.value }))
                  }
                  className="rounded-sm mt-1"
                />
              </div>
            </div>
            <div>
              <Label>Status</Label>
              <Select
                value={form.status}
                onValueChange={(v) => setForm((p) => ({ ...p, status: v }))}
              >
                <SelectTrigger className="rounded-sm mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              className="w-full rounded-sm"
              onClick={handleSave}
              data-testid="save-plan-btn"
            >
              {editing ? "Update" : "Create"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CouponsPage() {
  const [coupons, setCoupons] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    code: "",
    discountType: "percentage",
    value: "",
    expiryDate: "",
    usageLimit: "100",
  });
  useEffect(() => {
    fetchCoupons();
  }, []);
  const fetchCoupons = async () => {
    try {
      const r = await api.get("/admin/coupons");
      setCoupons(r.data.coupons);
    } catch {}
  };
  const handleSave = async () => {
    try {
      await api.post("/admin/coupons", {
        ...form,
        value: parseFloat(form.value),
        usageLimit: parseInt(form.usageLimit),
      });
      toast.success("Created");
      setShowForm(false);
      fetchCoupons();
    } catch {
      toast.error("Failed");
    }
  };
  return (
    <div className="space-y-6" data-testid="admin-coupons-page">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold font-heading">Coupons</h1>
        <Button
          size="sm"
          className="rounded-sm"
          onClick={() => {
            setForm({
              code: "",
              discountType: "percentage",
              value: "",
              expiryDate: "",
              usageLimit: "100",
            });
            setShowForm(true);
          }}
          data-testid="add-coupon-btn"
        >
          <Plus className="h-4 w-4 mr-1" />
          Add Coupon
        </Button>
      </div>
      <Card className="bg-card border-border rounded-sm">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Value</TableHead>
              <TableHead>Expiry</TableHead>
              <TableHead>Usage</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {coupons.map((c) => (
              <TableRow key={c.id}>
                <TableCell className="font-mono font-bold">{c.code}</TableCell>
                <TableCell>{c.discountType}</TableCell>
                <TableCell>
                  {c.discountType === "percentage"
                    ? `${c.value}%`
                    : `\u20B9${c.value}`}
                </TableCell>
                <TableCell>{c.expiryDate?.slice(0, 10) || "N/A"}</TableCell>
                <TableCell>
                  {c.usageCount}/{c.usageLimit}
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive"
                    onClick={async () => {
                      await api.delete(`/admin/coupons/${c.id}`);
                      fetchCoupons();
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-card border-border rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Create Coupon</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Code</Label>
              <Input
                value={form.code}
                onChange={(e) =>
                  setForm((p) => ({ ...p, code: e.target.value.toUpperCase() }))
                }
                className="rounded-sm mt-1"
                data-testid="coupon-code"
              />
            </div>
            <div>
              <Label>Discount Type</Label>
              <Select
                value={form.discountType}
                onValueChange={(v) =>
                  setForm((p) => ({ ...p, discountType: v }))
                }
              >
                <SelectTrigger className="rounded-sm mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Percentage</SelectItem>
                  <SelectItem value="fixed">Fixed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Value</Label>
              <Input
                type="number"
                value={form.value}
                onChange={(e) =>
                  setForm((p) => ({ ...p, value: e.target.value }))
                }
                className="rounded-sm mt-1"
                data-testid="coupon-value"
              />
            </div>
            <div>
              <Label>Expiry Date</Label>
              <Input
                type="date"
                value={form.expiryDate}
                onChange={(e) =>
                  setForm((p) => ({ ...p, expiryDate: e.target.value }))
                }
                className="rounded-sm mt-1"
              />
            </div>
            <div>
              <Label>Usage Limit</Label>
              <Input
                type="number"
                value={form.usageLimit}
                onChange={(e) =>
                  setForm((p) => ({ ...p, usageLimit: e.target.value }))
                }
                className="rounded-sm mt-1"
              />
            </div>
            <Button
              className="w-full rounded-sm"
              onClick={handleSave}
              data-testid="save-coupon-btn"
            >
              Create Coupon
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OrganizationsPage() {
  const [orgs, setOrgs] = useState([]);
  const [preventSignup, setPreventSignup] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [deletingOrg, setDeletingOrg] = useState(null);
  const [permanentDelete, setPermanentDelete] = useState(false);

  useEffect(() => {
    fetchOrgs();
    fetchConfig();
  }, []);

  const fetchOrgs = () => {
    api
      .get("/admin/organizations")
      .then((r) => setOrgs(r.data.organizations))
      .catch(console.error);
  };

  const fetchConfig = () => {
    api
      .get("/admin/config")
      .then((r) => {
        setPreventSignup(r.data.config?.preventSignup || false);
      })
      .catch(console.error)
      .finally(() => setLoadingConfig(false));
  };

  const handleToggleSignup = async () => {
    try {
      const targetState = !preventSignup;
      await api.put("/admin/config", { preventSignup: targetState });
      setPreventSignup(targetState);
      toast.success(
        targetState
          ? "New registrations have been disabled"
          : "New registrations have been enabled",
      );
    } catch (err) {
      toast.error("Failed to update registration settings");
    }
  };

  const confirmDelete = async () => {
    if (!deletingOrg) return;
    try {
      await api.delete(`/admin/organizations/${deletingOrg.id}`, {
        params: { permanent: permanentDelete },
      });
      toast.success(
        permanentDelete
          ? "Organization and all data permanently deleted"
          : "Organization soft deleted",
      );
      setDeletingOrg(null);
      setPermanentDelete(false);
      fetchOrgs();
    } catch (err) {
      toast.error("Failed to delete organization");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-orgs-page">
      <h1 className="text-2xl font-bold font-heading">Organizations</h1>

      {/* Registration Settings */}
      <div className="grid grid-cols-1 gap-4">
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-6 flex items-center justify-between">
            <div>
              <h3 className="font-heading font-bold text-lg">
                Registration Settings
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Prevent new organization signups globally on the platform.
              </p>
            </div>
            <Button
              variant={preventSignup ? "destructive" : "default"}
              onClick={handleToggleSignup}
              disabled={loadingConfig}
              className="rounded-sm"
              data-testid="toggle-signup-btn"
            >
              {preventSignup ? "Enable Signups" : "Disable Signups"}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Organizations Table */}
      <Card className="bg-card border-border rounded-sm">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Members</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Actions</TableHead>
              <TableHead>Is Deleted</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orgs.map((o) => (
              <TableRow key={o.id}>
                <TableCell className="font-medium">{o.name}</TableCell>
                <TableCell>{o.email}</TableCell>
                <TableCell className="font-mono">{o.memberCount}</TableCell>
                <TableCell>
                  <Badge
                    className={`${o.subscriptionStatus === "active" ? "bg-green-500/20 text-green-400" : o.subscriptionStatus === "trial" ? "bg-blue-500/20 text-blue-400" : "bg-red-500/20 text-red-400"} border-0 rounded-sm text-xs`}
                  >
                    {o.subscriptionStatus}
                  </Badge>
                </TableCell>
                <TableCell>{o.createdAt?.slice(0, 10)}</TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive"
                    onClick={() => {
                      setDeletingOrg(o);
                      setPermanentDelete(false);
                    }}
                    data-testid={`delete-org-${o.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
                <TableCell>{o.isDeleted ? "Yes" : "No"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deletingOrg !== null}
        onOpenChange={(open) => {
          if (!open) setDeletingOrg(null);
        }}
      >
        <DialogContent className="bg-card border-border rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-destructive flex items-center gap-2">
              <Trash2 className="h-5 w-5" /> Delete Organization
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm">
              Are you sure you want to delete organization{" "}
              <strong className="text-foreground">{deletingOrg?.name}</strong>?
            </p>

            <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded">
              <input
                type="checkbox"
                id="permanentDelete"
                checked={permanentDelete}
                onChange={(e) => setPermanentDelete(e.target.checked)}
                className="h-4 w-4 rounded border-border text-primary focus:ring-primary bg-card"
                data-testid="permanent-delete-checkbox"
              />
              <label
                htmlFor="permanentDelete"
                className="text-xs font-medium text-destructive cursor-pointer select-none"
              >
                Permanently delete organization and all associated data
                (members, branches, payments, workouts, etc.)
              </label>
            </div>

            <p className="text-xs text-muted-foreground">
              {permanentDelete
                ? "Warning: This action is irreversible. All records belonging to this organization will be purged from the database."
                : "Note: This will perform a soft delete. The organization will be disabled, but data will remain in the database."}
            </p>

            <div className="flex gap-3 justify-end pt-2">
              <Button
                variant="ghost"
                size="sm"
                className="rounded-sm"
                onClick={() => setDeletingOrg(null)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                size="sm"
                className="rounded-sm"
                onClick={confirmDelete}
                data-testid="confirm-delete-btn"
              >
                Delete Organization
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function WhatsAppConfigPage() {
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    creditPrice: 0.5,
    minPurchase: 100,
    maxPurchase: 10000,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [configRes, statsRes] = await Promise.all([
        api.get("/whatsapp/admin/config"),
        api.get("/whatsapp/admin/stats"),
      ]);
      setConfig(configRes.data.config);
      setStats(statsRes.data);
      setForm({
        creditPrice: configRes.data.config?.creditPrice || 0.5,
        minPurchase: configRes.data.config?.minPurchase || 100,
        maxPurchase: configRes.data.config?.maxPurchase || 10000,
      });
    } catch (err) {
      console.error("Failed to fetch data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await api.put("/whatsapp/admin/config", {
        creditPrice: parseFloat(form.creditPrice),
        minPurchase: parseInt(form.minPurchase),
        maxPurchase: parseInt(form.maxPurchase),
      });
      toast.success("Configuration saved");
      fetchData();
    } catch (err) {
      toast.error("Failed to save configuration");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-whatsapp-page">
      <h1 className="text-2xl font-bold font-heading">
        WhatsApp Configuration
      </h1>

      {/* Platform Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-card border-border rounded-sm">
            <CardContent className="p-4">
              <MessageCircle className="h-5 w-5 text-green-400" />
              <p className="text-2xl font-bold font-heading mt-2">
                {stats.totalMessages}
              </p>
              <p className="text-xs text-muted-foreground">Total Messages</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border rounded-sm">
            <CardContent className="p-4">
              <CreditCard className="h-5 w-5 text-blue-400" />
              <p className="text-2xl font-bold font-heading mt-2">
                {stats.totalCreditsPurchased}
              </p>
              <p className="text-xs text-muted-foreground">Credits Purchased</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border rounded-sm">
            <CardContent className="p-4">
              <DollarSign className="h-5 w-5 text-emerald-400" />
              <p className="text-2xl font-bold font-heading mt-2">
                ₹{stats.totalRevenue?.toLocaleString()}
              </p>
              <p className="text-xs text-muted-foreground">WhatsApp Revenue</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border rounded-sm">
            <CardContent className="p-4">
              <Building2 className="h-5 w-5 text-purple-400" />
              <p className="text-2xl font-bold font-heading mt-2">
                {stats.activeAccounts}
              </p>
              <p className="text-xs text-muted-foreground">Active Accounts</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Message Stats by Status */}
      {stats?.statusCounts && Object.keys(stats.statusCounts).length > 0 && (
        <Card className="bg-card border-border rounded-sm">
          <CardHeader>
            <CardTitle className="text-sm font-heading">
              Message Status Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {Object.entries(stats.statusCounts).map(([status, count]) => (
                <div
                  key={status}
                  className="text-center p-3 bg-secondary/30 rounded"
                >
                  <p className="text-xl font-bold">{count}</p>
                  <p className="text-xs text-muted-foreground">{status}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pricing Configuration */}
      <Card className="bg-card border-border rounded-sm">
        <CardHeader>
          <CardTitle className="text-sm font-heading flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Credit Pricing Configuration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs">Price per Credit (₹)</Label>
              <Input
                type="number"
                step="0.01"
                min="0.1"
                value={form.creditPrice}
                onChange={(e) =>
                  setForm({ ...form, creditPrice: e.target.value })
                }
                className="rounded-sm mt-1"
                data-testid="credit-price-input"
              />
            </div>
            <div>
              <Label className="text-xs">Min Purchase (credits)</Label>
              <Input
                type="number"
                min="1"
                value={form.minPurchase}
                onChange={(e) =>
                  setForm({ ...form, minPurchase: e.target.value })
                }
                className="rounded-sm mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Max Purchase (credits)</Label>
              <Input
                type="number"
                min="100"
                value={form.maxPurchase}
                onChange={(e) =>
                  setForm({ ...form, maxPurchase: e.target.value })
                }
                className="rounded-sm mt-1"
              />
            </div>
          </div>
          <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/20 rounded">
            <p className="text-xs text-amber-500">
              <strong>Note:</strong> Changing the credit price will only affect
              new purchases. Existing credits will remain unaffected.
            </p>
          </div>
          <Button
            className="mt-4 rounded-sm"
            onClick={handleSave}
            data-testid="save-config-btn"
          >
            Save Configuration
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export default function AdminDashboard() {
  return (
    <AdminLayout>
      <Routes>
        <Route index element={<AdminHome />} />
        <Route path="plans" element={<PlansPage />} />
        <Route path="coupons" element={<CouponsPage />} />
        <Route path="organizations" element={<OrganizationsPage />} />
        <Route path="whatsapp" element={<WhatsAppConfigPage />} />
        <Route path="whatsapp-templates" element={<WhatsappTemplates />} />
      </Routes>
    </AdminLayout>
  );
}
